import React, { useState, useEffect } from 'react';
import {
  Users,
  TrendingUp,
  Activity,
  Building2,
  FileText,
  ChevronRight,
  Search,
  Download,
  Eye,
  Flag,
  MessageSquare,
  X,
  AlertTriangle,
  Trash2,
  User,
  Briefcase,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import Dashboard from './Dashboard';
import TaxReports from './TaxReports';
import Settings from './Settings';

interface Business {
  id: string;
  name: string;
  description: string;
}

interface BusinessUser {
  id: string;
  user_id: number;
  business_id: string;
  telegram_username: string;
  telegram_first_name: string;
  telegram_last_name: string;
  is_active: boolean;
}

interface Receipt {
  id: string;
  user_id: string;
  merchant_name: string;
  total_amount: number;
  currency: string;
  receipt_date: string;
  category: string;
  status: string;
}

interface TeamLeaderDashboardProps {
  currentUserId?: string | null;
}

export default function TeamLeaderDashboard({ currentUserId }: TeamLeaderDashboardProps) {
  const [viewMode, setViewMode] = useState<'personal' | 'management'>('management');
  const [personalPage, setPersonalPage] = useState<'dashboard' | 'tax-reports' | 'settings'>('dashboard');
  const [activeTab, setActiveTab] = useState<'overview' | 'businesses' | 'users' | 'receipts'>('overview');
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [users, setUsers] = useState<BusinessUser[]>([]);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<BusinessUser | null>(null);
  const [editingUser, setEditingUser] = useState(false);
  const [userIdMapping, setUserIdMapping] = useState<Map<string, number>>(new Map());

  useEffect(() => {
    loadData();
  }, [currentUserId]);

  const loadData = async () => {
    try {
      let businessesQuery = supabase.from('businesses').select('*');

      if (currentUserId) {
        businessesQuery = businessesQuery.eq('team_leader_id', currentUserId);
      }

      const businessesResult = await businessesQuery;
      const myBusinesses = businessesResult.data || [];
      const businessIds = myBusinesses.map(b => b.id);

      let usersResult;
      let receiptsResult;

      if (businessIds.length > 0 && currentUserId) {
        [usersResult, receiptsResult] = await Promise.all([
          supabase.from('user_businesses').select('*').in('business_id', businessIds),
          supabase.from('receipts').select('*').limit(100),
        ]);

        const telegramIds = (usersResult.data || []).map((u: any) => u.user_id);

        const { data: usersMapping } = await supabase
          .from('users')
          .select('id, telegram_id')
          .in('telegram_id', telegramIds);

        const mapping = new Map<string, number>();
        (usersMapping || []).forEach((u: any) => {
          mapping.set(u.id, u.telegram_id);
        });
        setUserIdMapping(mapping);

        const userUuids = (usersMapping || []).map((u: any) => u.id);
        const filteredReceipts = (receiptsResult.data || []).filter((r: any) =>
          userUuids.includes(r.user_id)
        );

        setUsers(usersResult.data || []);
        setReceipts(filteredReceipts);
      } else if (!currentUserId) {
        [usersResult, receiptsResult] = await Promise.all([
          supabase.from('user_businesses').select('*'),
          supabase.from('receipts').select('*').limit(100),
        ]);

        setUsers(usersResult.data || []);
        setReceipts(receiptsResult.data || []);
      } else {
        setUsers([]);
        setReceipts([]);
      }

      setBusinesses(myBusinesses);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateUserStatus = async (userId: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('user_businesses')
        .update({ is_active: isActive })
        .eq('id', userId);

      if (error) throw error;
      await loadData();
      setEditingUser(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user status');
    }
  };

  const removeUserFromBusiness = async (userId: string) => {
    if (!confirm('Are you sure you want to remove this user from the business?')) return;

    try {
      const { error } = await supabase
        .from('user_businesses')
        .delete()
        .eq('id', userId);

      if (error) throw error;
      await loadData();
      setSelectedUser(null);
    } catch (error) {
      console.error('Error removing user:', error);
      alert('Failed to remove user');
    }
  };

  const exportReceipts = () => {
    const csvContent = [
      ['Date', 'Merchant', 'Amount', 'Currency', 'Category', 'Status', 'User ID'].join(','),
      ...filteredReceipts.map(r => [
        new Date(r.receipt_date).toLocaleDateString(),
        r.merchant_name,
        r.total_amount,
        r.currency,
        r.category,
        r.status,
        userIdMapping.get(r.user_id) || r.user_id
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `receipts-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const filteredUsers = users.filter(
    (user) =>
      (!selectedBusiness || user.business_id === selectedBusiness) &&
      (user.telegram_username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.telegram_first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.user_id.toString().includes(searchTerm))
  );

  const filteredReceipts = receipts.filter((receipt) => {
    if (!selectedBusiness) return true;
    const businessUsers = users.filter((u) => u.business_id === selectedBusiness);
    const telegramIds = businessUsers.map(u => u.user_id);
    const receiptTelegramId = userIdMapping.get(receipt.user_id);
    return receiptTelegramId && telegramIds.includes(receiptTelegramId);
  });

  const totalReceipts = filteredReceipts.length;
  const totalUsers = filteredUsers.length;
  const activeUsers = filteredUsers.filter((u) => u.is_active).length;
  const totalAmount = filteredReceipts.reduce((sum, r) => sum + parseFloat(r.total_amount?.toString() || '0'), 0);

  const StatCard = ({ icon: Icon, title, value, subtitle, color }: any) => (
    <div className="card">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">{title}</p>
          <p className={`text-3xl font-bold ${color}`}>{value}</p>
          {subtitle && <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 ${color.replace('text', 'bg')}/10 rounded-lg flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-brand-primary rounded-lg flex items-center justify-center">
                <span className="text-xl font-bold text-white">L</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">LytSpeed Receipts</h1>
                <p className="text-sm text-gray-900 dark:text-gray-100">
                  {viewMode === 'personal' ? 'My Receipts' : 'Team Management'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
                <button
                  onClick={() => setViewMode('personal')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-all ${
                    viewMode === 'personal'
                      ? 'bg-white dark:bg-gray-700 text-brand-primary shadow-sm'
                      : 'text-gray-700 dark:text-gray-100 hover:text-brand-primary dark:hover:text-brand-primary'
                  }`}
                >
                  <User className="w-4 h-4" />
                  <span>My Receipts</span>
                </button>
                <button
                  onClick={() => setViewMode('management')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-all ${
                    viewMode === 'management'
                      ? 'bg-white dark:bg-gray-700 text-brand-primary shadow-sm'
                      : 'text-gray-700 dark:text-gray-100 hover:text-brand-primary dark:hover:text-brand-primary'
                  }`}
                >
                  <Briefcase className="w-4 h-4" />
                  <span>Team Management</span>
                </button>
              </div>
              <div className="h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-brand-primary" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-100">Team Leader</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {viewMode === 'personal' && (
        <div className="flex flex-col min-h-screen">
          <nav className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center h-16">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setPersonalPage('dashboard')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                      personalPage === 'dashboard'
                        ? 'bg-brand-primary text-white shadow-brand'
                        : 'text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    <span>Dashboard</span>
                  </button>
                  <button
                    onClick={() => setPersonalPage('tax-reports')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                      personalPage === 'tax-reports'
                        ? 'bg-brand-primary text-white shadow-brand'
                        : 'text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    <span>Tax Reports</span>
                  </button>
                  <button
                    onClick={() => setPersonalPage('settings')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                      personalPage === 'settings'
                        ? 'bg-brand-primary text-white shadow-brand'
                        : 'text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    <span>Settings</span>
                  </button>
                </div>
              </div>
            </div>
          </nav>

          <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
            {import.meta.env.VITE_DEVELOPMENT_MODE === 'true' && (
              <div className="mb-6 bg-gradient-to-r from-orange-100 to-red-100 border-2 border-orange-300 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-gray-900">Preview Mode: Team Leader (Personal View)</p>
                    <p className="text-sm text-gray-700">
                      {currentUserId
                        ? 'Viewing personal receipts for the selected team leader. Dashboard shows only their own receipts.'
                        : 'Team Leaders can also access their personal receipt management interface. Use the dev panel to select a specific team leader to view as.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
            {personalPage === 'dashboard' && <Dashboard devModeUserId={currentUserId} />}
            {personalPage === 'tax-reports' && <TaxReports devModeUserId={currentUserId} />}
            {personalPage === 'settings' && <Settings showBotSetup={false} />}
          </main>

          <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 py-4">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <p className="text-sm text-gray-800 dark:text-gray-200">
                Managed by <span className="font-semibold text-brand-primary">Lyt Brox Pte Ltd</span>
              </p>
            </div>
          </footer>
        </div>
      )}

      {viewMode === 'management' && (
        <div className="max-w-7xl mx-auto px-6 py-8">
          {import.meta.env.VITE_DEVELOPMENT_MODE === 'true' && (
            <div className="mb-6 bg-gradient-to-r from-orange-100 to-red-100 border-2 border-orange-300 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-gray-900">Preview Mode: Team Leader</p>
                  <p className="text-sm text-gray-700">
                    {currentUserId
                      ? `Viewing as specific team leader. Only showing their assigned businesses and team data.`
                      : 'Can manage team members, businesses, and view team receipts.'}
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="flex gap-4 mb-6 overflow-x-auto pb-2">
            {[
              { id: 'overview', label: 'Overview', icon: TrendingUp },
              { id: 'businesses', label: 'My Businesses', icon: Building2 },
              { id: 'users', label: 'Team Members', icon: Users },
              { id: 'receipts', label: 'Receipts', icon: FileText },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-brand-primary text-white shadow-lg'
                    : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
        </div>

        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                icon={Building2}
                title="My Businesses"
                value={businesses.length}
                subtitle="Assigned businesses"
                color="text-blue-600"
              />
              <StatCard
                icon={Users}
                title="Team Members"
                value={totalUsers}
                subtitle={`${activeUsers} active`}
                color="text-green-600"
              />
              <StatCard
                icon={FileText}
                title="Total Receipts"
                value={totalReceipts}
                subtitle="All time"
                color="text-purple-600"
              />
              <StatCard
                icon={TrendingUp}
                title="Total Amount"
                value={`$${totalAmount.toFixed(2)}`}
                subtitle="Processed"
                color="text-emerald-600"
              />
            </div>

            <div className="card-gradient">
              <h3 className="text-lg font-semibold mb-2">Quick Overview</h3>
              <p className="text-white/80 text-sm mb-4">Manage your assigned businesses and team members</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  onClick={() => setActiveTab('businesses')}
                  className="bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-lg text-left transition-all backdrop-blur-sm border border-white/20"
                >
                  <Building2 className="w-5 h-5 mb-1" />
                  <p className="font-medium">View Businesses</p>
                  <p className="text-xs text-white/70">{businesses.length} businesses assigned</p>
                </button>
                <button
                  onClick={() => setActiveTab('users')}
                  className="bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-lg text-left transition-all backdrop-blur-sm border border-white/20"
                >
                  <Users className="w-5 h-5 mb-1" />
                  <p className="font-medium">Manage Team</p>
                  <p className="text-xs text-white/70">{totalUsers} team members</p>
                </button>
              </div>
            </div>

            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
              <div className="space-y-3">
                {receipts.slice(0, 5).map((receipt) => (
                  <div key={receipt.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-brand-primary/10 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-brand-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{receipt.merchant_name}</p>
                        <p className="text-sm text-gray-800 dark:text-gray-200">
                          {receipt.currency} {receipt.total_amount}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-900 dark:text-gray-100">{new Date(receipt.receipt_date).toLocaleDateString()}</p>
                      <span className="text-xs badge-info">{receipt.category}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'businesses' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">My Businesses</h2>
              <p className="text-sm text-gray-800 dark:text-gray-200">{businesses.length} businesses</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {businesses.map((business) => {
                const businessUsers = users.filter((u) => u.business_id === business.id);
                const telegramIds = businessUsers.map(u => u.user_id);
                const businessReceipts = receipts.filter((r) => {
                  const receiptTelegramId = userIdMapping.get(r.user_id);
                  return receiptTelegramId && telegramIds.includes(receiptTelegramId);
                });

                return (
                  <div key={business.id} className="card hover:shadow-lg transition-shadow cursor-pointer">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-brand-primary/10 rounded-lg flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-brand-primary" />
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{business.name}</h3>
                    <p className="text-sm text-gray-900 dark:text-gray-100 mb-4 line-clamp-2">
                      {business.description || 'No description'}
                    </p>

                    <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                      <div className="text-center flex-1">
                        <p className="text-2xl font-bold text-brand-primary">{businessUsers.length}</p>
                        <p className="text-xs text-gray-800 dark:text-gray-200">Members</p>
                      </div>
                      <div className="w-px h-10 bg-gray-200"></div>
                      <div className="text-center flex-1">
                        <p className="text-2xl font-bold text-green-600">{businessReceipts.length}</p>
                        <p className="text-xs text-gray-800 dark:text-gray-200">Receipts</p>
                      </div>
                    </div>
                  </div>
                );
              })}

              {businesses.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-800 dark:text-gray-200">No businesses assigned yet</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Team Members</h2>
              <div className="flex gap-3">
                <select
                  value={selectedBusiness || ''}
                  onChange={(e) => setSelectedBusiness(e.target.value || null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                >
                  <option value="">All Businesses</option>
                  {businesses.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="card">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-700 dark:text-gray-300" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name or Telegram ID..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                />
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-100">User</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-100">Telegram ID</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-100">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-100">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-gray-900">
                              {user.telegram_first_name} {user.telegram_last_name}
                            </p>
                            <p className="text-sm text-gray-800 dark:text-gray-200">@{user.telegram_username || 'N/A'}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 font-mono text-sm">{user.user_id}</td>
                        <td className="py-3 px-4">
                          {user.is_active ? (
                            <span className="badge-success">Active</span>
                          ) : (
                            <span className="badge-error">Inactive</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setEditingUser(true);
                            }}
                            className="text-brand-primary hover:text-brand-secondary text-sm font-medium"
                          >
                            Manage
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredUsers.length === 0 && (
                  <div className="text-center py-8 text-gray-800 dark:text-gray-200">No team members found</div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'receipts' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Receipts</h2>
              <button
                onClick={exportReceipts}
                className="btn-primary flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>

            <div className="card overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Merchant</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Amount</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Category</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">User</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Status</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReceipts.slice(0, 50).map((receipt) => (
                    <tr key={receipt.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-300">
                        {new Date(receipt.receipt_date).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{receipt.merchant_name}</td>
                      <td className="py-3 px-4 font-semibold text-gray-900 dark:text-white">
                        {receipt.currency} {receipt.total_amount}
                      </td>
                      <td className="py-3 px-4">
                        <span className="badge-info">{receipt.category}</span>
                      </td>
                      <td className="py-3 px-4 font-mono text-sm text-gray-900 dark:text-gray-300">{userIdMapping.get(receipt.user_id) || 'N/A'}</td>
                      <td className="py-3 px-4">
                        <span className="badge-success">{receipt.status}</span>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => setSelectedReceipt(receipt)}
                          className="text-brand-primary hover:text-brand-secondary text-sm font-medium flex items-center gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredReceipts.length === 0 && (
                <div className="text-center py-8 text-gray-800 dark:text-gray-200 dark:text-gray-400">No receipts found</div>
              )}
            </div>

            {selectedReceipt && (
              <ReceiptViewModal
                receipt={selectedReceipt}
                onClose={() => setSelectedReceipt(null)}
                onFlag={() => setShowFlagModal(true)}
                onComment={() => setShowCommentModal(true)}
              />
            )}

            {showFlagModal && selectedReceipt && (
              <FlagModal
                receiptId={selectedReceipt.id}
                onClose={() => setShowFlagModal(false)}
              />
            )}

            {showCommentModal && selectedReceipt && (
              <CommentModal
                receiptId={selectedReceipt.id}
                onClose={() => setShowCommentModal(false)}
              />
            )}
          </div>
        )}

          {editingUser && selectedUser && (
            <UserManagementModal
              user={selectedUser}
              onClose={() => {
                setEditingUser(false);
                setSelectedUser(null);
              }}
              onUpdateStatus={updateUserStatus}
              onRemove={removeUserFromBusiness}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ReceiptViewModal({ receipt, onClose, onFlag, onComment }: any) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">Receipt Details</h3>
          <button onClick={onClose} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 dark:hover:text-gray-300">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-gray-400">Merchant</label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{receipt.merchant_name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-gray-400">Amount</label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {receipt.currency} {receipt.total_amount}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-gray-400">Date</label>
              <p className="text-gray-900 dark:text-white">{new Date(receipt.receipt_date).toLocaleDateString()}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-gray-400">Category</label>
              <p className="text-gray-900 dark:text-white">{receipt.category}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-gray-400">Status</label>
              <span className="badge-success">{receipt.status}</span>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-gray-400">User ID</label>
              <p className="text-gray-900 dark:text-white font-mono">{receipt.user_id}</p>
            </div>
          </div>

          {receipt.image_url && (
            <div>
              <label className="text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-gray-400 block mb-2">Receipt Image</label>
              <img
                src={receipt.image_url}
                alt="Receipt"
                className="w-full rounded-lg border border-gray-200 dark:border-gray-700"
              />
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onFlag}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              <Flag className="w-4 h-4" />
              Flag Receipt
            </button>
            <button
              onClick={onComment}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-accent transition-colors"
            >
              <MessageSquare className="w-4 h-4" />
              Add Comment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FlagModal({ receiptId, onClose }: any) {
  const [flagType, setFlagType] = useState('review_needed');
  const [flagReason, setFlagReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const { error } = await supabase.from('receipt_flags').insert({
        receipt_id: receiptId,
        admin_user_id: 'mock-admin-id',
        flag_type: flagType,
        flag_reason: flagReason,
      });

      if (error) throw error;
      onClose();
    } catch (error) {
      console.error('Error flagging receipt:', error);
      alert('Failed to flag receipt');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-orange-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">Flag Receipt</h3>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Flag Type</label>
            <select
              value={flagType}
              onChange={(e) => setFlagType(e.target.value)}
              className="input-field"
              required
            >
              <option value="review_needed">Review Needed</option>
              <option value="duplicate">Duplicate</option>
              <option value="invalid">Invalid</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Reason</label>
            <textarea
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value)}
              className="input-field"
              rows={4}
              placeholder="Describe why you're flagging this receipt..."
              required
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 btn-primary disabled:opacity-50"
            >
              {submitting ? 'Flagging...' : 'Flag Receipt'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CommentModal({ receiptId, onClose }: any) {
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const { error } = await supabase.from('receipt_comments').insert({
        receipt_id: receiptId,
        admin_user_id: 'mock-admin-id',
        comment_text: commentText,
      });

      if (error) throw error;
      onClose();
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-brand-primary/10 rounded-lg flex items-center justify-center">
            <MessageSquare className="w-6 h-6 text-brand-primary" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">Add Comment</h3>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Comment</label>
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              className="input-field"
              rows={4}
              placeholder="Add your comment about this receipt..."
              required
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 btn-primary disabled:opacity-50"
            >
              {submitting ? 'Adding...' : 'Add Comment'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UserManagementModal({ user, onClose, onUpdateStatus, onRemove }: any) {
  const [isActive, setIsActive] = useState(user.is_active);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">Manage Team Member</h3>
          <button onClick={onClose} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 dark:hover:text-gray-300">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="space-y-2">
              <div>
                <label className="text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-gray-400">Name</label>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {user.telegram_first_name} {user.telegram_last_name}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-gray-400">Username</label>
                <p className="text-gray-900 dark:text-white">@{user.telegram_username || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-gray-400">Telegram ID</label>
                <p className="text-gray-900 dark:text-white font-mono">{user.user_id}</p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Active Status</span>
                <p className="text-xs text-gray-800 dark:text-gray-200 dark:text-gray-400 mt-1">
                  {isActive ? 'User can access and submit receipts' : 'User is blocked from accessing the system'}
                </p>
              </div>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-5 h-5 text-brand-primary border-gray-300 rounded focus:ring-brand-primary"
              />
            </label>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-600">
            <button
              onClick={() => onUpdateStatus(user.id, isActive)}
              className="flex-1 btn-primary"
            >
              Save Changes
            </button>
            <button
              onClick={() => onRemove(user.id)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Remove
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
